import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Image,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'
import {imageTextTileTagConfig} from '@consteed/frontend/utils/richTextConfigurations'
// Icons
import {ChevronRightIcon} from '@consteed/overrides/components/icons'

const convertFocalPointToObjectPosition = (focalPoint) => {
    return Math.round(focalPoint.x * 100) + '% ' + Math.round(focalPoint.y * 100) + '%'
}

const ImageTextTileIcon = () => {
    const boxDimension = {base: '35px', md: '40px', xl: '70px'}

    const styles = {
        box: {
            alignItems: 'center',
            backgroundColor: 'black',
            borderColor: 'whiteAlpha.300',
            borderRadius: '100%',
            borderWidth: '1px',
            display: 'flex',
            position: 'absolute',
            top: '1em',
            right: '1em',
            height: boxDimension,
            width: boxDimension,
            shadow: 'icon'
        },
        icon: {
            margin: 'auto',
            color: 'yellow.400',
            width: '50%',
            height: '50%'
        }
    }

    return (
        <Box className={'image-text-tile-icon'} {...styles.box}>
            <ChevronRightIcon {...styles.icon} />
        </Box>
    )
}

/**
 * Image with text component
 *
 * @param {object} props
 * @param {string} - props.text - Text on Image.
 * @param {image} - props.image - Image.
 * @param {string} - props.alt - The image alt text shown by the component.
 * @param {boolean} - props.withHoverTransition - If image should be zoomed on hover
 * @param {string} - props.variant - display variant
 * @returns {React.ReactElement} - ImageTextTile component.
 */
export const ImageTextTile = ({
    text,
    image,
    alt,
    backgroundTheme,
    withHoverTransition,
    variant = 'default'
}) => {
    const textColor = backgroundTheme === 'light' ? 'black' : 'white'
    // backgrounds that are not light or dark will get a filter so they are dark
    const darken = ['light', 'dark'].indexOf(backgroundTheme) === -1
    const objectPosition =
        typeof image?.focalPoint?.x !== 'undefined'
            ? convertFocalPointToObjectPosition(image.focalPoint)
            : null
    const styles = useMultiStyleConfig('ImageTextTile', {variant, objectPosition})

    const animate = withHoverTransition
        ? {
              image: {
                  transition: '1.2s ease-out',
                  _groupActive: {transform: 'scale(1.2)'},
                  _groupHover: {transform: 'scale(1.2)'}
              },
              text: {
                  transition: '1.2s ease-out',
                  _groupActive: {fontSize: {base: '3xl', md: '4xl', xl: '5xl'}},
                  _groupHover: {fontSize: {base: '3xl', md: '4xl', xl: '5xl'}}
              }
          }
        : {}
    return (
        <Box
            className={'image-text-tile'}
            position={'relative'}
            width={'100%'}
            maxWidth={styles?.maxWidth}
            overflow={'hidden'}
        >
            <Box
                as="figure"
                className={'image-text-tile-figure'}
                position={'relative'}
                margin={0}
                width={'100%'}
                data-group
            >
                <picture>
                    {image?.src?.tablet && (
                        <source srcSet={image?.src?.tablet} media="(min-width: 48em)" />
                    )}
                    {image?.src?.desktop && (
                        <source srcSet={image?.src?.desktop} media="(min-width: 64em)" />
                    )}
                    <Image
                        className={'image-text-tile-image'}
                        data-testid={'image-text-tile-image'}
                        src={image?.src?.mobile ? image?.src?.mobile : image?.url}
                        ignoreFallback={true}
                        alt={alt}
                        title={alt}
                        __css={styles}
                        {...animate?.image}
                    />
                </picture>
                {darken && (
                    <Box
                        width="100%"
                        height="100%"
                        position="absolute"
                        top="0"
                        bgGradient="linear(to-t, blackAlpha.700, transparent)"
                    />
                )}
                <ImageTextTileIcon />
                {text && (
                    <Text as="figcaption">
                        <Box
                            className={'image-text-tile-heading-container'}
                            position={'absolute'}
                            bottom={'0'}
                            width={'100%'}
                            textAlign={'left'}
                            padding={'2rem'}
                        >
                            <Text
                                as="span"
                                className={'image-text-tile-heading-text'}
                                color={textColor}
                                fontSize={{base: '2xl', md: '3xl', xl: '4xl'}}
                                fontWeight={'300'}
                                {...animate?.text}
                            >
                                <RTE richText={text} tagOptions={imageTextTileTagConfig} />
                            </Text>
                        </Box>
                    </Text>
                )}
            </Box>
        </Box>
    )
}

ImageTextTile.propTypes = {
    text: PropTypes.string,
    image: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    alt: PropTypes.string,
    backgroundTheme: PropTypes.string,
    withHoverTransition: PropTypes.bool,
    variant: PropTypes.string
}

export default ImageTextTile
